// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useMemo } from 'react';

import axios, { type AxiosRequestConfig, type AxiosResponse, create as axiosCreate } from 'axios';
import rateLimit from 'axios-rate-limit';
import { jwtDecode } from 'jwt-decode';

import type { EvolveJwtToken } from '../Misc/types';
import type { AuthReturn } from '../User/types';

import { getLocalStorage, setLocalStorage } from './localStorage';

export const SIGN_OUT_EVENT = 'LOGOUT';

export const doSignOut = (routeToLocation?: string) => {
  window.dispatchEvent(new CustomEvent(SIGN_OUT_EVENT));
  localStorage.clear();
  sessionStorage.clear();
  window.location.replace(routeToLocation ?? window.location.href);
};

type RefreshReturn = Pick<AuthReturn, 'idToken' | 'accessToken' | 'expiration'>;

const axiosClient = rateLimit(
  axiosCreate({
    paramsSerializer(params) {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        // `undefined` and empty string are not valid parameters, but null is.
        if (value === undefined || value === '') continue;

        if (Array.isArray(value)) {
          const filtered = value.filter((v) => v !== undefined && v !== '');
          if (filtered.length > 0) {
            // Our Backend expects comma separated lists for URL parameters that are arrays
            searchParams.append(key, filtered.join(','));
          }
        } else {
          searchParams.append(key, value);
        }
      }
      // Any param-specific encoding happens in helpers/ag-grid/convertAgGridRequest.ts,
      // or should be applied manually on search strings via `encodeURIComponent`
      return decodeURIComponent(searchParams.toString());
    },
  }),
  { maxRPS: 9 },
);

export type EvolveApi = 'admin' | 'shop' | 'usage-stats' | 'usage-stats-retriever' | 'material-analytics';

export type EvolveRoute = `${EvolveApi}/${string}`;

type CallType = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
type NoBodyCalls = 'GET' | 'DELETE';

const methodMap = {
  GET: axiosClient.get,
  POST: axiosClient.post,
  PATCH: axiosClient.patch,
  PUT: axiosClient.put,
  DELETE: axiosClient.delete,
} as const;

declare const encodedJwtSymbol: unique symbol;
export type EncodedJwt = string & { [encodedJwtSymbol]: never };

let accessTokenPromise: Promise<EncodedJwt> | null = null;

export const refreshAccessTokenFromLocalStorage = async (
  getBaseApiUrl: GetBaseUrl,
  forceSignOut = true,
): Promise<EncodedJwt | null> => {
  const refreshToken = getLocalStorage('EVOLVE_REFRESH_TOKEN');
  if (!refreshToken) {
    if (forceSignOut) {
      doSignOut();
    }
    return null;
  }
  const userName = getLocalStorage('EVOLVE_TOKEN_USERNAME');
  const baseUrl = await getBaseApiUrl();
  accessTokenPromise ??= axios
    .post<{ refreshToken: EncodedJwt }, AxiosResponse<RefreshReturn>>(`${baseUrl}/admin/authentication/refreshToken`, {
      refreshToken,
      userName,
    })
    .then(async ({ data: { accessToken } }) => {
      setLocalStorage('EVOLVE_ACCESS_TOKEN', accessToken as EncodedJwt);
      return accessToken as EncodedJwt;
    })
    .catch((err) => {
      doSignOut();
      throw err;
    })
    .finally(() => (accessTokenPromise = null));
  return accessTokenPromise;
};

const getTokenFromLocalStorage = async (getBaseApiUrl: GetBaseUrl) => {
  const accessToken = (await accessTokenPromise) ?? getLocalStorage('EVOLVE_ACCESS_TOKEN');
  if (!accessToken) return null;
  const decoded = jwtDecode(accessToken) as EvolveJwtToken;
  const expired = !!decoded.exp && decoded.exp * 1000 + 5000 < Date.now();
  if (!expired) return accessToken;
  const newToken = await refreshAccessTokenFromLocalStorage(getBaseApiUrl);
  if (newToken) setLocalStorage('EVOLVE_ACCESS_TOKEN', newToken);
  return newToken;
};

const responseMapper = <ReturnType>(res: AxiosResponse<ReturnType>): ReturnType =>
  res.status === 204 ? (undefined as ReturnType) : (res.data ?? (res as ReturnType));

const buildUrl = async (getBaseApiUrl: GetBaseUrl, path: EvolveRoute) => {
  const baseApiUrl = await getBaseApiUrl(path);
  if (baseApiUrl.includes('localhost')) {
    return `${baseApiUrl}/${path.replace(/^[a-z]+\//g, '')}`;
  }
  return `${baseApiUrl}/${path}`;
};

const callBuilder = async <R, T>(
  params: {
    path: EvolveRoute;
    config?: AxiosRequestConfig<T>;
  } & (
    | {
        method: Extract<CallType, NoBodyCalls>;
        body?: never;
      }
    | {
        method: Exclude<CallType, NoBodyCalls>;
        body: T;
      }
  ),
  getToken: () => Promise<string | null>,
  getBaseApiUrl: GetBaseUrl,
): Promise<R> => {
  const { path, config: customConfig, method, body } = params;
  // Not all endpoints require auth. If we don't have a JWT, perform the call anyway.
  const token = await getToken();

  const headers = {
    Authorization: token ? `Bearer ${token}` : undefined,
    Accept: '*/*',
  };
  const config: AxiosRequestConfig<T> = {
    ...customConfig,
    headers: {
      ...headers,
      // oxlint-disable-next-line typescript/no-misused-spread
      ...customConfig?.headers,
    },
  };

  const url = await buildUrl(getBaseApiUrl, path);

  if (method === 'GET' || method === 'DELETE') {
    return await methodMap[method](url, config).then(responseMapper);
  }
  return await methodMap[method](url, body, config).then(responseMapper);
};

type GetBaseUrl = (api?: EvolveRoute | EvolveApi) => Promise<string>;

export type EvolveApiContextType = {
  /**
   * Returns the API token for backend calls. Must also handle any refresh token logic.
   *
   * If not provided, will store the token in local storage
   * and refresh the token when necessary.
   */
  getToken?: () => Promise<string | null>;
  /** Returns the base URL for backend calls. */
  getBaseApiUrl: GetBaseUrl;
  /** Called upon an error to allow the caller to display the error. */
  showError: (error: string) => void;
};

export const EvolveApiContext = React.createContext<EvolveApiContextType>({
  getBaseApiUrl: async () => 'https://api.evolvemep.com',
  showError:
    // oxlint-disable-next-line no-console
    console.error,
});

export const useBaseEvolveApi = <R, T>() => {
  const { getBaseApiUrl, showError, getToken } = React.useContext(EvolveApiContext);
  const getTokenWrapped = useCallback(
    () => (getToken ? getToken() : getTokenFromLocalStorage(getBaseApiUrl)),
    [getBaseApiUrl, getToken],
  );

  const get = useCallback(
    (path: EvolveRoute, config?: AxiosRequestConfig<T>) =>
      callBuilder<R, T>({ path, config, method: 'GET' }, getTokenWrapped, getBaseApiUrl),
    [getTokenWrapped, getBaseApiUrl],
  );

  const post = useCallback(
    (path: EvolveRoute, body: T, config?: AxiosRequestConfig<T>) =>
      callBuilder<R, T>({ path, body, config, method: 'POST' }, getTokenWrapped, getBaseApiUrl),
    [getTokenWrapped, getBaseApiUrl],
  );

  const patch = useCallback(
    (path: EvolveRoute, body: T, config?: AxiosRequestConfig<T>) =>
      callBuilder<R, T>({ path, body, config, method: 'PATCH' }, getTokenWrapped, getBaseApiUrl),
    [getTokenWrapped, getBaseApiUrl],
  );

  const put = useCallback(
    (path: EvolveRoute, body: T, config?: AxiosRequestConfig<T>) =>
      callBuilder<R, T>({ path, body, config, method: 'PUT' }, getTokenWrapped, getBaseApiUrl),
    [getTokenWrapped, getBaseApiUrl],
  );

  const apiDelete = useCallback(
    (path: EvolveRoute, config?: AxiosRequestConfig<T>) =>
      callBuilder<R, T>({ path, config, method: 'DELETE' }, getTokenWrapped, getBaseApiUrl),
    [getTokenWrapped, getBaseApiUrl],
  );

  return useMemo(
    () => ({ get, post, patch, put, apiDelete, showError }),
    [apiDelete, showError, get, patch, post, put],
  );
};
